// src/utils/validation.js

// Email validation
export const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// Phone validation (flexible format)
export const validatePhone = (phone) => {
    if (!phone) return true; // Phone is optional
    // Allows various formats: (555) 123-4567, 555-123-4567, +1 555 123 4567, etc.
    const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
    return phoneRegex.test(phone);
};

// Password validation
export const validatePassword = (password) => {
    return {
        isValid: password.length >= 6,
        errors: password.length < 6 ? ['Password must be at least 6 characters long'] : []
    };
};

// Name validation
export const validateName = (name, fieldName = 'Name') => {
    const trimmedName = name.trim();
    const errors = [];

    if (!trimmedName) {
        errors.push(`${fieldName} is required`);
    } else if (trimmedName.length < 2) {
        errors.push(`${fieldName} must be at least 2 characters long`);
    } else if (!/^[a-zA-Z\s'-]+$/.test(trimmedName)) {
        errors.push(`${fieldName} can only contain letters, spaces, hyphens, and apostrophes`);
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

// ZIP code validation (US format)
export const validateZipCode = (zipCode) => {
    if (!zipCode) return { isValid: true, errors: [] }; // Optional field

    const zipRegex = /^\d{5}(-\d{4})?$/;
    return {
        isValid: zipRegex.test(zipCode),
        errors: zipRegex.test(zipCode) ? [] : ['Please enter a valid ZIP code (e.g., 12345 or 12345-6789)']
    };
};

// State validation (US state codes)
export const validateState = (state) => {
    if (!state) return { isValid: true, errors: [] }; // Optional field

    const stateRegex = /^[A-Z]{2}$/i;
    return {
        isValid: stateRegex.test(state),
        errors: stateRegex.test(state) ? [] : ['Please enter a valid 2-letter state code (e.g., CA, NY)']
    };
};

// Comprehensive form validation for signup
export const validateSignupForm = (formData) => {
    const errors = {};

    // Required fields
    if (!formData.email) errors.email = 'Email is required';
    else if (!validateEmail(formData.email)) errors.email = 'Please enter a valid email address';

    if (!formData.password) errors.password = 'Password is required';
    else {
        const passwordValidation = validatePassword(formData.password);
        if (!passwordValidation.isValid) errors.password = passwordValidation.errors[0];
    }

    if (!formData.confirmPassword) errors.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match';

    if (!formData.firstName) errors.firstName = 'First name is required';
    else {
        const firstNameValidation = validateName(formData.firstName, 'First name');
        if (!firstNameValidation.isValid) errors.firstName = firstNameValidation.errors[0];
    }

    if (!formData.lastName) errors.lastName = 'Last name is required';
    else {
        const lastNameValidation = validateName(formData.lastName, 'Last name');
        if (!lastNameValidation.isValid) errors.lastName = lastNameValidation.errors[0];
    }

    // Optional fields with validation
    if (formData.phone && !validatePhone(formData.phone)) {
        errors.phone = 'Please enter a valid phone number';
    }

    if (formData.zipCode) {
        const zipValidation = validateZipCode(formData.zipCode);
        if (!zipValidation.isValid) errors.zipCode = zipValidation.errors[0];
    }

    if (formData.state) {
        const stateValidation = validateState(formData.state);
        if (!stateValidation.isValid) errors.state = stateValidation.errors[0];
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Format phone number for display
export const formatPhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    if (cleaned.length === 11 && cleaned[0] === '1') {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }

    return phoneNumber; // Return original if format doesn't match
};

export const removeDots = function(email) {
    if (!email || !email.includes('@')) {
        return email;
    }
    const [localPart, domain] = email.split('@');
    const cleanedLocal = localPart.replace(/\./g, '');
    return cleanedLocal + '@' + domain;
}